/**
 * Lightweight error reporter that sends errors to a Supabase edge function.
 * Debounces identical errors and silently fails — must never crash the app.
 */

import { supabase } from '@/integrations/supabase/client';
import i18next from 'i18next';
import { toast } from 'sonner';

interface ErrorContext {
  component?: string;
  [key: string]: unknown;
}

// Track recently reported errors to debounce duplicates
const recentErrors = new Map<string, number>();
const DEBOUNCE_MS = 10_000; // Don't report the same error within 10s

function getErrorKey(message: string, component?: string): string {
  return `${component || ''}:${message}`;
}

/**
 * Browser-extension errors surface on our window (content scripts that wrap
 * fetch/XHR, inject code, etc.) but are NOT our bugs — they're pure noise that
 * inflates the daily error digest (e.g. "Failed to fetch" thrown inside an
 * extension's wrapped fetch). Drop anything whose ORIGINATING frame (the top of
 * the stack) is an extension scheme; only the originating frame is checked so a
 * genuine app error that merely passed through an extension-wrapped API is still
 * reported. Also drop the detail-less cross-origin "Script error.".
 */
const MAX_REASON_CHARS = 500;

export interface ErrorDescription {
  message: string;
  stack?: string;
  /** typeof the rejection reason when it was not an Error; absent otherwise. */
  reasonType?: string;
}

/**
 * Turns whatever was thrown or rejected into a message and a stack the digest
 * can act on. A rejection with no argument used to reach the database as the
 * six letters "undefined" with no stack: 50 such rows in September, and no way
 * to tell what happened. Non-Error reasons now carry their type, a bounded
 * serialisation and the stack of the capture site.
 */
export function describeError(error: unknown): ErrorDescription {
  if (error instanceof Error) return { message: error.message, stack: error.stack };
  const reasonType = error === null ? 'null' : typeof error;
  let text: string;
  if (error !== null && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string') {
    const e = error as { name?: unknown; message: string };
    text = `${typeof e.name === 'string' ? e.name : 'Object'}: ${e.message}`;
  } else if (typeof error === 'string') {
    text = JSON.stringify(error);
  } else {
    try {
      text = JSON.stringify(error) ?? String(error);
    } catch {
      text = String(error);
    }
  }
  if (text.length > MAX_REASON_CHARS) text = text.slice(0, MAX_REASON_CHARS) + '...';
  return {
    message: `non-Error rejection (${reasonType}): ${text}`,
    stack: new Error('capture site').stack,
    reasonType,
  };
}

/**
 * Messages that browsers, extensions and mail link scanners raise on our
 * window without any extension frame in the stack, so they landed in the
 * digest as ours. Kept to exact, well-known strings so a genuine app error
 * can never match by accident.
 */
const NOISE_MESSAGE_PATTERNS: readonly RegExp[] = [
  // Scripts iOS browsers inject into every page: Firefox and Brave (which is
  // built on it) use __firefox__, Brave's wallet sets window.ethereum, Chrome
  // uses __gCrWeb. None of these names exists in our code.
  /\b__firefox__\b/,
  /\bwindow\.ethereum\b/,
  /\b__gCrWeb\b/,
  /No Listener: tabs:outgoing\.message\.ready/,
  /Object Not Found Matching Id:\d+, MethodName:/,
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,
  // A browser refusing to autoplay media. Every play() of ours catches it;
  // these come from embeds and extensions (Firefox wording, then Chrome's).
  /^(NotAllowedError: )?The play method is not allowed by the user agent or the platform in the current context/,
  /^(NotAllowedError: )?play\(\) failed because the user didn't interact with the document first/,
  // Supabase auth taking its storage lock from another tab of the same site;
  // the auth client retries on its own and the session stays intact.
  /^(AbortError: )?Lock was stolen by another request$/,
];

/**
 * Crawler renderers and automated browsers get the prerendered page by design
 * and fetch scripts under their own budget, so a chunk or locale they fail to
 * load is not an app defect. Headless crawlers that dress up as an iPhone or a
 * desktop Chrome still set navigator.webdriver. Same list as error-digest.
 */
const AUTOMATED_UA = /googlebot|googleother|google-inspectiontool|adsbot|mediapartners-google|bingbot|applebot|yandexbot|duckduckbot|baiduspider|petalbot|bot\/|crawler|spider|headlesschrome|lighthouse/i;

export function isAutomatedBrowser(userAgent: string, webdriver: boolean): boolean {
  return webdriver || AUTOMATED_UA.test(userAgent);
}

/**
 * APIs a browser can lack by design: Safari in Lockdown Mode removes
 * FileReader and WebGL, Safari before 16.4 has no OffscreenCanvas. The user
 * sees why (the 3D fallback panel, the file-reading notice below); there is
 * nothing for the digest to fix.
 */
const UNSUPPORTED_API = /Can't find variable: (FileReader|OffscreenCanvas)\b|\b(FileReader|OffscreenCanvas) is not defined|does not support WebGL/;

export function isUnsupportedBrowserError(message: string): boolean {
  return UNSUPPORTED_API.test(message);
}

/**
 * A fetch the network refused, rejected with only the browser's own words and
 * no frame of ours: offline, a content blocker, a dropped connection.
 */
const BARE_NETWORK_FAILURE = /^(TypeError: )?(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)$/;

export function isBareNetworkFailure(message: string, stack?: string): boolean {
  const hasFrame = !!stack && /\n\s*at\s|@\S/.test(stack);
  return !hasFrame && BARE_NETWORK_FAILURE.test(message);
}

export function isThirdPartyNoise(message: string, stack?: string): boolean {
  if (NOISE_MESSAGE_PATTERNS.some((re) => re.test(message))) return true;
  if (message === 'Script error.') return true;
  if (!stack) return false;
  const firstFrame = stack
    .split('\n')
    .find((l) => /\s(at|@)\s|^\s*at\s|@http|@chrome|@moz/.test(l) || /-extension:\/\//.test(l));
  if (!firstFrame) return false;
  // Code that runs as <anonymous> was injected into the page (an extension's
  // content script, an eval'd third-party snippet); ours always runs from a
  // file under /assets/.
  if (/[\s(@]<anonymous>:\d+:\d+/.test(firstFrame)) return true;
  return /(chrome|moz|safari-web|safari|ms-browser)-extension:\/\//.test(firstFrame);
}

/**
 * Report an error to the backend. Safe to call from anywhere —
 * never throws, never blocks, never crashes.
 */
export async function reportError(
  error: unknown,
  context?: ErrorContext,
): Promise<void> {
  try {
    const { message, stack, reasonType } = describeError(error);
    const component = context?.component;

    // The daily digest is for PRODUCTION user errors only. Dev builds (and
    // locally/LAN-served builds) emit transient errors during active
    // development — HMR module-shape changes, half-saved edits, "Importing
    // binding X is not found", "Rendered more hooks…" mid-edit — which are seen
    // live in the console and must NOT pollute the prod digest. Gate on the dev
    // build flag plus localhost / private-LAN hostnames (covers a prod build
    // served via `vite preview` on the LAN during phone testing).
    if (import.meta.env.DEV) return;
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    if (
      /^(localhost|127\.0\.0\.1|0\.0\.0\.0|::1)$/.test(host) ||
      /^(10|192\.168)\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(host)
    ) {
      return;
    }

    // Drop browser-extension / cross-origin noise before it hits the digest.
    if (isThirdPartyNoise(message, stack)) return;
    if (typeof navigator !== 'undefined' && isAutomatedBrowser(navigator.userAgent ?? '', navigator.webdriver === true)) return;
    if (isUnsupportedBrowserError(message)) return;
    if (isBareNetworkFailure(message, stack)) return;

    // Debounce identical errors
    const key = getErrorKey(message, component);
    const lastReported = recentErrors.get(key);
    if (lastReported && Date.now() - lastReported < DEBOUNCE_MS) {
      return;
    }
    recentErrors.set(key, Date.now());

    // Prune old entries to prevent memory leak. Two-stage: first drop
    // anything past the debounce window, then if everything is "fresh" enforce
    // a hard cap by evicting the oldest insertion-ordered entries. Without
    // the hard cap a runaway loop firing 200 distinct errors inside the
    // debounce window would grow the map unbounded.
    if (recentErrors.size > 100) {
      const cutoff = Date.now() - DEBOUNCE_MS;
      for (const [k, v] of recentErrors) {
        if (v < cutoff) recentErrors.delete(k);
      }
      while (recentErrors.size > 100) {
        const oldestKey = recentErrors.keys().next().value;
        if (oldestKey === undefined) break;
        recentErrors.delete(oldestKey);
      }
    }

    // Build payload
    const payload = {
      error_message: message,
      error_stack: stack,
      component,
      context: {
        ...(context ?? {}),
        component: undefined,
        ...(reasonType ? { reasonType } : {}),
      },
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent:
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };

    // Get auth token if available (don't block on auth)
    const session = await supabase.auth.getSession();
    const token = session?.data?.session?.access_token;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    if (!supabaseUrl || !apikey) return;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      apikey,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Fire and forget — don't await in most callers
    fetch(`${supabaseUrl}/functions/v1/log-error`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    }).catch(() => {
      // Silently ignore — error reporter itself must not crash
    });
  } catch {
    // Silently ignore any error in the reporter itself
  }
}

/**
 * Install a global handler for unhandled promise rejections.
 * Call once in main.tsx.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('unhandledrejection', (event) => {
    noticeMissingFileReader(event.reason);
    reportError(event.reason, { component: 'unhandledrejection' });
  });

  window.addEventListener('error', (event) => {
    const error = event.error ?? new Error(String(event.message));
    noticeMissingFileReader(error);
    reportError(error, { component: 'window.onerror' });
  });
}

let fileReaderNoticeShown = false;

/** Without FileReader no upload, import or logo can be read; say why once. */
function noticeMissingFileReader(error: unknown): void {
  if (fileReaderNoticeShown || typeof FileReader !== 'undefined') return;
  if (!/FileReader/.test(describeError(error).message)) return;
  fileReaderNoticeShown = true;
  toast.error(i18next.t('common:browser_file_reading_blocked', {
    defaultValue: 'This browser does not let websites read files (Safari in Lockdown Mode, for example), so the file cannot be opened. Use Chrome, Firefox or Edge, or turn off Lockdown Mode for this website.',
  }), { duration: 12000 });
}
